import { motion } from 'motion/react';
import { BookOpen, ArrowRight, MapPin, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useBrand } from '../context/BrandContext';

export default function LogTeaser() {
  const { resolveMediaUrl, stories, content } = useBrand();
  const [featuredPosts, setFeaturedPosts] = useState<any[]>([]);

  useEffect(() => {
    const published = (stories || []).filter(s => s.published !== false);
    const featured = published.filter(s => s.isFeatured).slice(0, 3);
    const displayPosts = featured.length > 0 ? featured : published.slice(0, 3);
    setFeaturedPosts(displayPosts);
  }, [stories]);

  if (featuredPosts.length === 0) return null;

  const mainPost = featuredPosts[0];

  return (
    <section className="py-32 bg-ows-white overflow-hidden relative" id="expedition-log">
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-ows-teal/20 to-transparent"></div>
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(18,52,59,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(18,52,59,0.1)_1px,transparent_1px)] bg-[size:100px_100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 text-ows-teal mb-10">
              <div className="h-[1px] w-8 bg-ows-teal"></div>
              <span className="text-[10px] font-bold tracking-[0.4em] font-mono uppercase">{content.logLabel}</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-display font-light uppercase tracking-widest text-ows-dark leading-tight mb-8">
              {content.logTitle} <br />
              <span className="text-ows-teal italic font-normal lowercase font-display tracking-normal">{content.logSubtitle}</span>
            </h2>
            
            <p className="text-sm md:text-base text-[#141414]/60 font-mono tracking-tighter leading-relaxed mb-12 max-w-xl uppercase">
              {content.logQuote}
            </p>

            <div className="space-y-4 mb-16 relative">
              <div className="absolute left-[19px] top-4 bottom-4 w-[1px] bg-ows-teal/20 hidden sm:block"></div>
              {featuredPosts.map((post, idx) => (
                <Link 
                  key={post.id}
                  to={`/adventures?id=${post.id}`}
                  className="flex items-start gap-8 p-6 bg-[#FAFAFA] border border-ows-teal/10 hover:border-ows-teal/40 hover:bg-white transition-all group relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-transparent group-hover:bg-ows-teal transition-colors"></div>
                  <div className="hidden sm:flex h-10 w-10 mt-1 shrink-0 items-center justify-center bg-white border border-ows-teal/20 shadow-sm relative z-10 text-ows-teal group-hover:bg-ows-teal group-hover:text-white transition-colors">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-mono font-bold text-ows-dark uppercase mb-2 group-hover:text-ows-teal transition-colors tracking-widest leading-relaxed">
                      {post.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[9px] font-bold uppercase tracking-widest text-ows-dark/40">
                      <span>{new Date(post.date).toLocaleDateString()}</span>
                      {post.location && (
                        <>
                          <span className="text-ows-teal/40">•</span>
                          <span className="text-ows-orange">{post.location}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 mt-2 ml-auto shrink-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 text-ows-teal transition-all" />
                </Link>
              ))}
            </div>

            <Link 
              to="/adventures"
              className="inline-flex items-center justify-center gap-4 px-12 py-5 bg-ows-dark text-white text-[10px] font-bold tracking-[0.3em] uppercase hover:bg-[#2a2a2a] transition-all group w-full sm:w-auto shadow-xl"
            >
              Access Complete Archives
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform text-white/50" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative h-full"
          >
            <div className="aspect-[3/4] lg:aspect-auto lg:h-[800px] overflow-hidden bg-ows-dark relative">
              <img 
                src={resolveMediaUrl(mainPost.image) || undefined} 
                alt="Expedition" 
                className="w-full h-full object-cover opacity-80 mix-blend-luminosity hover:mix-blend-normal transition-all duration-700"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 border border-white/10 m-4 pointer-events-none"></div>
              
              <div className="absolute top-8 left-8 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-ows-safety-green animate-pulse"></div>
                <span className="text-[8px] font-mono font-bold text-white uppercase tracking-widest bg-ows-dark/50 px-2 py-1 backdrop-blur-sm">LATEST DISPATCH</span>
              </div>
              
              <div className="absolute bottom-8 left-8 right-8">
                <div className="bg-ows-dark/80 backdrop-blur-md p-8 border border-white/10">
                  <h3 className="text-xl md:text-2xl font-display font-light text-white uppercase tracking-[0.2em] leading-tight mb-4 group-hover:text-ows-teal transition-colors">
                    {mainPost.title}
                  </h3>
                  <Link 
                    to={`/adventures?id=${mainPost.id}`}
                    className="inline-flex items-center gap-2 text-[9px] font-mono font-bold uppercase tracking-widest text-ows-teal hover:text-white transition-colors"
                  >
                    View Dispatch <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Edge Indicators */}
            <div className="absolute -top-2 -left-2 w-4 h-4 border-t border-l border-ows-teal"></div>
            <div className="absolute -top-2 -right-2 w-4 h-4 border-t border-r border-ows-teal"></div>
            <div className="absolute -bottom-2 -left-2 w-4 h-4 border-b border-l border-ows-teal"></div>
            <div className="absolute -bottom-2 -right-2 w-4 h-4 border-b border-r border-ows-teal"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
